// src/app/core/api/api.service.ts
// Generated from OpenAPI 3.1 specification
// DO NOT EDIT MANUALLY - Regenerate using OpenAPI spec
// Last updated: 2026-06-06

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import * as M from './models';
import * as WpModels from '../../features/wp-sites/models/wp-site.models';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly apiUrl: string;

  constructor(private http: HttpClient) {
    const baseUrl = environment.apiUrl || 'http://localhost:8080';
    this.apiUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  }

  // ==========================================
  // PRIVATE HELPER METHODS
  // ==========================================

  private createParams(params: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();

    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== null && value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach(item => httpParams = httpParams.append(key, item.toString()));
        } else {
          httpParams = httpParams.set(key, value.toString());
        }
      }
    });

    return httpParams;
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });
  }

  // ==========================================
  // DEVELOPERS
  // ==========================================

  /**
   * Get Developer
   * GET /api/v1/developers
   */
  getDeveloper(): Observable<M.DeveloperDto> {
    return this.http.get<M.DeveloperDto>(`${this.apiUrl}/api/v1/developers`);
  }

  /**
   * Creates Developer
   * POST /api/v1/developers
   */
  createDeveloper(data: M.CreateDeveloperCommand): Observable<M.DeveloperDto> {
    return this.http.post<M.DeveloperDto>(
      `${this.apiUrl}/api/v1/developers`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Updates Developer
   * You can change Headquarter assignment but to update its data use PlaceController
   * PUT /api/v1/developers
   */
  updateDeveloper(data: M.DeveloperCommand): Observable<M.DeveloperDto> {
    return this.http.put<M.DeveloperDto>(
      `${this.apiUrl}/api/v1/developers`,
      data,
      { headers: this.getHeaders() }
    );
  }

  // ==========================================
  // PLACES (Selling Points)
  // ==========================================

  /**
   * Returns place by ID
   * GET /api/v1/places/{id}
   */
  getPlace(id: number): Observable<M.PlaceDto> {
    return this.http.get<M.PlaceDto>(`${this.apiUrl}/api/v1/places/${id}`);
  }

  /**
   * Returns places for Developer (Selling Points)
   * GET /api/v1/selling-points
   */
  getSellingPoints(): Observable<M.SellingPointDto[]> {
    return this.http.get<M.SellingPointDto[]>(`${this.apiUrl}/api/v1/selling-points`);
  }

  /**
   * Creates a new Selling Point
   * POST /api/v1/selling-points
   */
  createSellingPoint(data: M.SellingPointRequest): Observable<M.PlaceDto> {
    return this.http.post<M.PlaceDto>(
      `${this.apiUrl}/api/v1/selling-points`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Updates place
   * PUT /api/v1/selling-points/{placeId}
   */
  updateSellingPoint(placeId: number, data: M.SellingPointRequest): Observable<M.PlaceDto> {
    return this.http.put<M.PlaceDto>(
      `${this.apiUrl}/api/v1/selling-points/${placeId}`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Get Selling Points assigned to the Investment
   * GET /api/v1/investments/{investmentId}/selling-points
   */
  getInvestmentSellingPoints(investmentId: number): Observable<M.PlaceDto[]> {
    return this.http.get<M.PlaceDto[]>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/selling-points`
    );
  }

  /**
   * Creates a new place for the developer and assigns it to the investment
   * POST /api/v1/investments/{investmentId}/selling-points
   */
  createSellingPointForInvestment(investmentId: number, data: M.SellingPointRequest): Observable<M.PlaceDto> {
    return this.http.post<M.PlaceDto>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/selling-points`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Assign Selling Point to investment
   * POST /api/v1/investments/{investmentId}/selling-points/{placeId}/assign
   */
  assignSellingPointToInvestment(placeId: number, investmentId: number): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/selling-points/${placeId}/assign`,
      null
    );
  }

  /**
   * Removes all assignments and assigns Selling Points to Investment
   * PUT /api/v1/investments/{investmentId}/places/update-assignments
   */
  assignPlacesToInvestment(investmentId: number, placeIds: number[]): Observable<void> {
    return this.http.put<void>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/places/update-assignments`,
      placeIds,
      { headers: this.getHeaders() }
    );
  }

  // ==========================================
  // INVESTMENTS
  // ==========================================

  /**
   * Get all developer's investments
   * GET /api/v1/investments
   */
  getInvestments(): Observable<M.InvestmentWithCountsDto[]> {
    return this.http.get<M.InvestmentWithCountsDto[]>(
      `${this.apiUrl}/api/v1/investments`
    );
  }

  /**
   * Get Investment by ID
   * GET /api/v1/investments/{investmentId}
   */
  getInvestment(investmentId: number): Observable<M.InvestmentDetailsWithCountsDto> {
    return this.http.get<M.InvestmentDetailsWithCountsDto>(
      `${this.apiUrl}/api/v1/investments/${investmentId}`
    );
  }

  /**
   * Get Investment select option - use for dropdowns
   * GET /api/v1/investments/select-options
   */
  getInvestmentSelectOptions(): Observable<M.InvestmentSelectOptionDto[]> {
    return this.http.get<M.InvestmentSelectOptionDto[]>(
      `${this.apiUrl}/api/v1/investments/select-options`
    );
  }

  /**
   * Create Investment for given Developer
   * POST /api/v1/investments
   */
  createInvestment(data: M.CreateInvestmentRequest): Observable<number> {
    return this.http.post<number>(
      `${this.apiUrl}/api/v1/investments`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Update Investment
   * PUT /api/v1/investments/{investmentId}
   */
  updateInvestment(investmentId: number, data: M.InvestmentRequest): Observable<void> {
    return this.http.put<void>(
      `${this.apiUrl}/api/v1/investments/${investmentId}`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Update Investment Location
   * PUT /api/v1/investments/{investmentId}/location
   */
  updateInvestmentLocation(investmentId: number, data: M.InvestmentAddressRequest): Observable<void> {
    return this.http.put<void>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/location`,
      data,
      { headers: this.getHeaders() }
    );
  }

  // OPENAPI-GEN:BEGIN building-api-methods
  /**
   * Create Building in Investment
   * POST /api/v1/investments/{investmentId}/building
   */
  createBuilding(investmentId: number, data: M.CreateBuildingRequest): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/building`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Update Building in Investment
   * PUT /api/v1/investments/{investmentId}/buildings/{buildingId}
   */
  updateBuilding(
    investmentId: number,
    buildingId: number,
    data: M.BuildingRequest
  ): Observable<void> {
    return this.http.put<void>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/buildings/${buildingId}`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Create Building Address
   * POST /api/v1/investments/{investmentId}/buildings/{buildingId}/addresses
   */
  createBuildingAddress(
    investmentId: number,
    buildingId: number,
    data: M.BuildingAddressRequest
  ): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/buildings/${buildingId}/addresses`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Update Building Address
   * PUT /api/v1/investments/{investmentId}/buildings/{buildingId}/addresses/{buildingAddressId}
   */
  updateBuildingAddress(
    investmentId: number,
    buildingId: number,
    buildingAddressId: number,
    data: M.BuildingAddressRequest
  ): Observable<void> {
    return this.http.put<void>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/buildings/${buildingId}/addresses/${buildingAddressId}`,
      data,
      { headers: this.getHeaders() }
    );
  }
  // OPENAPI-GEN:END building-api-methods

  /**
   * Patch Parking Field (FAKE)
   */
  patchParkingField(investmentId: number, parkingId: number, fieldKey: string, value: any): Observable<any> {
    return new Observable(observer => {
      this.getParkingDetails(investmentId, parkingId).subscribe({
        next: (current) => {
          // Build request with known fields
          const request: any = {
            parkingTypeId: current.parkingType?.id,
            number: current.number,
            size: current.size,
            floor: current.floor,
            status: current.status
          };

          // Update the specific field
          request[fieldKey] = value;

          this.updateParking(investmentId, parkingId, request).subscribe({
            next: (result) => {
              observer.next(result);
              observer.complete();
            },
            error: (err) => observer.error(err)
          });
        },
        error: (err) => observer.error(err)
      });
    });
  }

  /**
   * Patch Ancillary Field (FAKE)
   */
  patchAncillaryField(investmentId: number, ancillaryId: number, fieldKey: string, value: any): Observable<any> {
    return new Observable(observer => {
      this.getAncillaryDetails(investmentId, ancillaryId).subscribe({
        next: (current) => {
          // Build request with known fields
          const request: any = {
            ancillaryTypeId: current.ancillaryType?.id,
            number: current.number,
            size: current.size,
            floor: current.floor,
            status: current.status
          };

          // Update the specific field
          request[fieldKey] = value;

          this.updateAncillary(investmentId, ancillaryId, request).subscribe({
            next: (result) => {
              observer.next(result);
              observer.complete();
            },
            error: (err) => observer.error(err)
          });
        },
        error: (err) => observer.error(err)
      });
    });
  }

  // ==========================================
  // APARTMENTS
  // ==========================================

  /**
   * Get Apartment List - with current price, without versions
   * GET /api/v1/investments/{investmentId}/apartments/list-as-of
   */
  getApartments(investmentId: number): Observable<M.ApartmentListItemView[]> {
    return this.http.get<M.ApartmentListItemView[]>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/apartments/list-as-of`
    );
  }

  /**
   * Get Apartment Details
   * GET /api/v1/investments/{investmentId}/apartments/{apartmentId}
   */
  getApartmentDetails(investmentId: number, apartmentId: number): Observable<M.ApartmentDetailsDto> {
    return this.http.get<M.ApartmentDetailsDto>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/apartments/${apartmentId}`
    );
  }

  /**
   * Alias for getApartmentDetails (backward compatibility)
   */
  getApartment(investmentId: number, apartmentId: number): Observable<M.ApartmentDetailsDto> {
    return this.getApartmentDetails(investmentId, apartmentId);
  }

  // OPENAPI-GEN:BEGIN patch-apartment-method
  /**
   * Patch Apartment (tri-state semantics)
   * PATCH /api/v1/investments/{investmentId}/apartments/{apartmentId}
   */
  patchApartment(
    investmentId: number,
    apartmentId: number,
    data: M.PatchApartmentRequest
  ): Observable<M.ApartmentDto> {
    return this.http.patch<M.ApartmentDto>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/apartments/${apartmentId}`,
      data,
      { headers: this.getHeaders() }
    );
  }
  // OPENAPI-GEN:END patch-apartment-method

  /**
   * Delete Apartment
   * DELETE /api/v1/investments/{investmentId}/apartments/{apartmentId}
   */
  deleteApartment(investmentId: number, apartmentId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/apartments/${apartmentId}`
    );
  }

  /**
   * Create Apartment Version for Apartment
   * POST /api/v1/investments/{investmentId}/apartments/{apartmentId}/versions
   */
  createApartmentVersion(
    investmentId: number,
    apartmentId: number,
    data: M.ApartmentVersionRequest
  ): Observable<M.ApartmentVersionDto> {
    return this.http.post<M.ApartmentVersionDto>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/apartments/${apartmentId}/versions`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Update Apartment Version
   * PUT /api/v1/investments/{investmentId}/apartments/{apartmentId}/versions/{versionId}
   */
  updateApartmentVersion(
    investmentId: number,
    apartmentIdOrVersionId: number,
    versionIdOrData: number | M.ApartmentVersionRequest,
    data?: M.ApartmentVersionRequest
  ): Observable<M.ApartmentVersionDto> {
    let url: string;
    let payload: M.ApartmentVersionRequest;

    if (data) {
      // New signature: (investmentId, apartmentId, versionId, data)
      url = `${this.apiUrl}/api/v1/investments/${investmentId}/apartments/${apartmentIdOrVersionId}/versions/${versionIdOrData}`;
      payload = data;
    } else {
      // Legacy signature: (investmentId, versionId, data)
      url = `${this.apiUrl}/api/v1/investments/${investmentId}/apartments/versions/${apartmentIdOrVersionId}`;
      payload = versionIdOrData as M.ApartmentVersionRequest;
    }

    return this.http.put<M.ApartmentVersionDto>(url, payload, { headers: this.getHeaders() });
  }

  // ==========================================
  // PARKINGS
  // ==========================================

  /**
   * Get Parkings for investment (with list-as-of view)
   * GET /api/v1/investments/{investmentId}/parkings/list-as-of
   */
  /**
   * Get Parkings for investment (with list-as-of view)
   * GET /api/v1/investments/{investmentId}/parkings/list-as-of
   */
  getParkingsAsOf(investmentId: number): Observable<M.ParkingListItemView[]> {
    return this.http.get<M.ParkingListItemView[]>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/parkings/list-as-of`
    );
  }

  /**
   * Get Parkings for investment
   * GET /api/v1/investments/{investmentId}/parkings
   */
  getParkings(investmentId: number): Observable<M.ParkingDetailsDto[]> {
    return this.http.get<M.ParkingDetailsDto[]>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/parkings`
    );
  }

  /**
   * Get Parking Details by ID
   * GET /api/v1/investments/{investmentId}/parkings/{parkingId}
   */
  getParkingDetails(investmentId: number, parkingId: number): Observable<M.ParkingDetailsDto> {
    return this.http.get<M.ParkingDetailsDto>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/parkings/${parkingId}`
    );
  }

  /**
   * Create Parking
   * POST /api/v1/investments/{investmentId}/parkings
   */
  createParking(investmentId: number, data: M.CreateParkingRequest): Observable<M.ParkingDetailsDto> {
    return this.http.post<M.ParkingDetailsDto>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/parkings`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Update Parking
   * PUT /api/v1/investments/{investmentId}/parkings/{parkingId}
   */
  updateParking(
    investmentId: number,
    parkingId: number,
    data: M.ParkingRequest
  ): Observable<M.ParkingDetailsDto> {
    return this.http.put<M.ParkingDetailsDto>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/parkings/${parkingId}`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Create Parking Version
   * POST /api/v1/investments/{investmentId}/parkings/{parkingId}/versions
   */
  createParkingVersion(
    investmentId: number,
    parkingId: number,
    data: M.ParkingVersionRequest
  ): Observable<M.ParkingVersionDto> {
    return this.http.post<M.ParkingVersionDto>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/parkings/${parkingId}/versions`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Update Parking Version
   * PUT /api/v1/investments/{investmentId}/parkings/{parkingId}/versions/{parkingVersionId}
   */
  updateParkingVersion(
    investmentId: number,
    parkingIdOrVersionId: number,
    parkingVersionIdOrData: number | M.ParkingVersionRequest,
    data?: M.ParkingVersionRequest
  ): Observable<M.ParkingVersionDto> {
    let url: string;
    let payload: M.ParkingVersionRequest;

    if (data) {
      // New signature: (investmentId, parkingId, versionId, data)
      url = `${this.apiUrl}/api/v1/investments/${investmentId}/parkings/${parkingIdOrVersionId}/versions/${parkingVersionIdOrData}`;
      payload = data;
    } else {
      // Legacy signature: (investmentId, versionId, data)
      url = `${this.apiUrl}/api/v1/investments/${investmentId}/parkings/versions/${parkingIdOrVersionId}`;
      payload = parkingVersionIdOrData as M.ParkingVersionRequest;
    }

    return this.http.put<M.ParkingVersionDto>(url, payload, { headers: this.getHeaders() });
  }

  // ==========================================
  // PARKING TYPES
  // ==========================================

  /**
   * Get Parking Types for investment
   * GET /api/v1/investments/{investmentId}/parking-types
   */
  getParkingTypes(investmentId: number): Observable<M.ParkingTypeDto[]> {
    return this.http.get<M.ParkingTypeDto[]>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/parking-types`
    );
  }

  /**
   * Create Parking Type
   * POST /api/v1/investments/{investmentId}/parking-types
   */
  createParkingType(investmentId: number, data: M.ParkingTypeRequest): Observable<M.ParkingTypeDto> {
    return this.http.post<M.ParkingTypeDto>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/parking-types`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Update Parking Type
   * PUT /api/v1/investments/{investmentId}/parking-types/{parkingTypeId}
   */
  updateParkingType(
    investmentId: number,
    parkingTypeId: number,
    data: M.ParkingTypeRequest
  ): Observable<M.ParkingTypeDto> {
    return this.http.put<M.ParkingTypeDto>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/parking-types/${parkingTypeId}`,
      data,
      { headers: this.getHeaders() }
    );
  }

  // ==========================================
  // ANCILLARY SPACES
  // ==========================================

  /**
   * Get Ancillary Spaces for investment (with list-as-of view)
   * GET /api/v1/investments/{investmentId}/ancillary-spaces/list-as-of
   */
  /**
   * Get Ancillary Spaces for investment (with list-as-of view)
   * GET /api/v1/investments/{investmentId}/ancillary-spaces/list-as-of
   */
  getAncillariesAsOf(investmentId: number): Observable<M.AncillaryListItemView[]> {
    return this.http.get<M.AncillaryListItemView[]>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/ancillary-spaces/list-as-of`
    );
  }

  /**
   * Get Ancillary Spaces for investment
   * GET /api/v1/investments/{investmentId}/ancillary-spaces
   */
  getAncillaries(investmentId: number): Observable<M.AncillaryDetailsDto[]> {
    return this.http.get<M.AncillaryDetailsDto[]>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/ancillary-spaces`
    );
  }

  /**
   * Get Ancillary Details by ID
   * GET /api/v1/investments/{investmentId}/ancillary-spaces/{ancillaryId}
   */
  getAncillaryDetails(investmentId: number, ancillaryId: number): Observable<M.AncillaryDetailsDto> {
    return this.http.get<M.AncillaryDetailsDto>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/ancillary-spaces/${ancillaryId}`
    );
  }

  /**
   * Create Ancillary Space
   * POST /api/v1/investments/{investmentId}/ancillary-spaces
   */
  createAncillary(investmentId: number, data: M.CreateAncillaryRequest): Observable<M.AncillaryDetailsDto> {
    return this.http.post<M.AncillaryDetailsDto>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/ancillary-spaces`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Update Ancillary Space
   * PUT /api/v1/investments/{investmentId}/ancillary-spaces/{ancillaryId}
   */
  updateAncillary(
    investmentId: number,
    ancillaryId: number,
    data: M.AncillaryRequest
  ): Observable<M.AncillaryDetailsDto> {
    return this.http.put<M.AncillaryDetailsDto>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/ancillary-spaces/${ancillaryId}`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Create Ancillary Version
   * POST /api/v1/investments/{investmentId}/ancillary-spaces/{ancillaryId}/versions
   */
  createAncillaryVersion(
    investmentId: number,
    ancillaryId: number,
    data: M.AncillaryVersionRequest
  ): Observable<M.AncillaryVersionDto> {
    return this.http.post<M.AncillaryVersionDto>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/ancillary-spaces/${ancillaryId}/versions`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Update Ancillary Version
   * PUT /api/v1/investments/{investmentId}/ancillary-spaces/versions/{ancillaryVersionId}
   */
  updateAncillaryVersion(
    investmentId: number,
    ancillaryVersionId: number,
    data: M.AncillaryVersionRequest
  ): Observable<M.AncillaryVersionDto> {
    return this.http.put<M.AncillaryVersionDto>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/ancillary-spaces/versions/${ancillaryVersionId}`,
      data,
      { headers: this.getHeaders() }
    );
  }

  // ==========================================
  // ANCILLARY SPACE TYPES
  // ==========================================

  /**
   * Get Ancillary Space Types for investment
   * GET /api/v1/investments/{investmentId}/ancillary-space-types
   */
  getAncillaryTypes(investmentId: number): Observable<M.AncillaryType[]> {
    return this.http.get<M.AncillaryType[]>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/ancillary-space-types`
    );
  }

  /**
   * Create Ancillary Space Type
   * POST /api/v1/investments/{investmentId}/ancillary-space-types
   */
  createAncillarySpaceType(investmentId: number, data: M.AncillaryTypeRequest): Observable<M.AncillaryType> {
    return this.http.post<M.AncillaryType>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/ancillary-space-types`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Update Ancillary Space Type
   * PUT /api/v1/investments/{investmentId}/ancillary-space-types/{ancillarySpaceTypeId}
   */
  updateAncillarySpaceType(
    investmentId: number,
    ancillarySpaceTypeId: number,
    data: M.AncillaryTypeRequest
  ): Observable<M.AncillaryType> {
    return this.http.put<M.AncillaryType>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/ancillary-space-types/${ancillarySpaceTypeId}`,
      data,
      { headers: this.getHeaders() }
    );
  }

  // ==========================================
  // ADDON RULES
  // ==========================================

  /**
   * Create Apartment Addon Rule
   * POST /api/v1/investments/{investmentId}/apartments/{apartmentId}/apartment-addon-rules
   */
  createApartmentAddonRule(
    investmentId: number,
    apartmentId: number,
    data: M.ApartmentAddonRuleRequest
  ): Observable<M.ApartmentAddonRuleDto> {
    return this.http.post<M.ApartmentAddonRuleDto>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/apartments/${apartmentId}/apartment-addon-rules`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Update Apartment Addon Rule
   * PUT /api/v1/investments/{investmentId}/apartments/{apartmentId}/apartment-addon-rules/{apartmentAddonRuleId}
   */
  updateApartmentAddonRule(
    investmentId: number,
    apartmentId: number,
    apartmentAddonRuleId: number,
    data: M.ApartmentAddonRuleRequest
  ): Observable<M.ApartmentAddonRuleDto> {
    return this.http.put<M.ApartmentAddonRuleDto>(
      `${this.apiUrl}/api/v1/investments/${investmentId}/apartments/${apartmentId}/apartment-addon-rules/${apartmentAddonRuleId}`,
      data,
      { headers: this.getHeaders() }
    );
  }

  // ==========================================
  // USERS
  // ==========================================

  /**
   * Get all users
   * GET /api/users
   */
  getAllUsers(): Observable<M.UserEntity[]> {
    return this.http.get<M.UserEntity[]>(`${this.apiUrl}/api/users`);
  }

  /**
   * Get users paginated
   * GET /api/users/paginated
   */
  getUsersPaginated(
    page: number = 0,
    size: number = 10,
    sort: string = 'id',
    direction: string = 'asc'
  ): Observable<M.PageResponseUserDto> {
    const params = this.createParams({ page, size, sort, direction });
    return this.http.get<M.PageResponseUserDto>(
      `${this.apiUrl}/api/users/paginated`,
      { params }
    );
  }

  /**
   * Get user by ID
   * GET /api/users/{id}
   */
  getUserById(id: number): Observable<M.UserEntity> {
    return this.http.get<M.UserEntity>(`${this.apiUrl}/api/users/${id}`);
  }

  /**
   * Create user
   * POST /api/users
   */
  createUser(data: M.CreateUserCommand): Observable<M.UserEntity> {
    return this.http.post<M.UserEntity>(
      `${this.apiUrl}/api/users`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Update user
   * PUT /api/users/{id}
   */
  updateUser(id: number, data: M.UserEntity): Observable<M.UserEntity> {
    return this.http.put<M.UserEntity>(
      `${this.apiUrl}/api/users/${id}`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Delete user
   * DELETE /api/users/{id}
   */
  deleteUser(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/api/users/${id}`);
  }

  // ==========================================
  // AUTH
  // ==========================================

  /**
   * Switch tenant (developer context)
   * POST /api/v1/auth/switch-tenant/{developerId}
   */
  switchTenant(developerId: number): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/api/v1/auth/switch-tenant/${developerId}`,
      null
    );
  }

   // ==========================================
  // GOV EXPORT
  // ==========================================

  /**
   * Export investment data
   * GET /api/v1/gov/export-data
   */
  exportInvestmentData(investmentId: number, date?: string): Observable<void> {
    const params = this.createParams({ investmentId, date });
    return this.http.get<void>(
      `${this.apiUrl}/api/v1/gov/export-data`,
      { params }
    );
  }

  /**
   * Quartz merge test endpoint
   * POST /api/v1/gov/export-data/blob/quartz-DAILY-job-test
   */
  quartzDailyJobTest(): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/api/v1/gov/export-data/blob/quartz-DAILY-job-test`,
      null
    );
  }

  /**
   * Quartz publish job test
   * POST /api/v1/gov/export-data/blob/quartz-PUBLISH-job-test
   */
  quartzPublishJobTest(): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/api/v1/gov/export-data/blob/quartz-PUBLISH-job-test`,
      null
    );
  }

  /**
   * Get GOV publication daily view
   * GET /api/v1/gov/view/publications/daily
   */
  getPublicationDailyRows(params?: {
    investmentId?: number;
    status?: string;
    page?: number;
    size?: number;
    sort?: string[];
  }): Observable<M.PageResponseGovPublicationDailyDto> {
    const httpParams = params ? this.createParams(params) : undefined;
    return this.http.get<M.PageResponseGovPublicationDailyDto>(
      `${this.apiUrl}/api/v1/gov/view/publications/daily`,
      { params: httpParams }
    );
  }

  /**
   * Get GOV resource daily view
   * GET /api/v1/gov/view/resources/daily
   */
  getResourceDailyRows(params?: {
    investmentId?: number;
    statusFilter?: string;
    page?: number;
    size?: number;
    sort?: string[];
  }): Observable<M.PageResponseGovResourceDailyDto> {
    const httpParams = params ? this.createParams(params) : undefined;
    return this.http.get<M.PageResponseGovResourceDailyDto>(
      `${this.apiUrl}/api/v1/gov/view/resources/daily`,
      { params: httpParams }
    );
  }

  /**
   * Get GOV resource versions for a specific day
   * GET /api/v1/gov/view/resources/daily/{investmentId}/{snapshotDate}/versions
   */
  getResourceVersions(investmentId: number, snapshotDate: string): Observable<M.GovResourceVersionDto[]> {
    return this.http.get<M.GovResourceVersionDto[]>(
      `${this.apiUrl}/api/v1/gov/view/resources/daily/${investmentId}/${snapshotDate}/versions`
    );
  }

  /**
   * Get GOV admin runs
   * GET /api/v1/gov/view/admin/runs
   */
  getAdminRuns(params?: {
    runKind?: string;
    runType?: string;
    status?: string;
    investmentId?: number;
    developerId?: number;
    page?: number;
    size?: number;
    sort?: string[];
  }): Observable<M.PageResponseGovAdminRunDto> {
    const httpParams = params ? this.createParams(params) : undefined;
    return this.http.get<M.PageResponseGovAdminRunDto>(
      `${this.apiUrl}/api/v1/gov/view/admin/runs`,
      { params: httpParams }
    );
  }

  /**
   * @deprecated Use getAdminRuns instead
   */
  getExecutions(params?: any): Observable<M.PageResponseGovExecutionDto> {
    const httpParams = params ? this.createParams(params) : undefined;
    return this.http.get<M.PageResponseGovExecutionDto>(
      `${this.apiUrl}/api/v1/gov/view/admin/runs`,
      { params: httpParams }
    );
  }

  /**
   * @deprecated Use getResourceDailyRows instead
   */
  getGovFiles(params?: any): Observable<M.PageResponseGovFileDto> {
    const httpParams = params ? this.createParams(params) : undefined;
    return this.http.get<M.PageResponseGovFileDto>(
      `${this.apiUrl}/api/v1/gov/view/resources/daily`,
      { params: httpParams }
    );
  }

  /**
   * Get GOV admin run details
   * GET /api/v1/gov/view/admin/runs/{runKind}/{runId}/details
   */
  getAdminRunDetails(runKind: string, runId: string, includeGlobal: boolean = false): Observable<M.GovAdminRunDto> {
    const params = this.createParams({ includeGlobal });
    return this.http.get<M.GovAdminRunDto>(
      `${this.apiUrl}/api/v1/gov/view/admin/runs/${runKind}/${runId}/details`,
      { params }
    );
  }

  /**
   * Get GOV admin run flow graph
   * GET /api/v1/gov/view/admin/runs/{runKind}/{runId}/flow-graph
   */
  getAdminRunFlowGraph(runKind: string, runId: string, includeGlobal: boolean = false): Observable<M.GovAdminRunFlowGraphDto> {
    const params = this.createParams({ includeGlobal });
    return this.http.get<M.GovAdminRunFlowGraphDto>(
      `${this.apiUrl}/api/v1/gov/view/admin/runs/${runKind}/${runId}/flow-graph`,
      { params }
    );
  }

  /**
   * Get Validation Logs for a specific run
   * GET /api/v1/gov/view/validation-logs/{runId}
   */
  getValidationLogs(runId: string): Observable<M.GovValidationLogDto[]> {
    return this.http.get<M.GovValidationLogDto[]>(
      `${this.apiUrl}/api/v1/gov/view/validation-logs/${runId}`
    );
  }

  /**
   * Enable GOV Integration for Investment
   * POST /api/v1/gov/investment/{investmentId}/enable
   * Returns validation result with errors, warnings count and runId
   * Note: Backend returns 400 with validation errors, but we treat it as success with data
   */
  enableGovIntegration(investmentId: number): Observable<M.GovEnableResponse> {
    console.log('API: enableGovIntegration called for investment', investmentId);

    return this.http.post<M.GovEnableResponse>(
      `${this.apiUrl}/api/v1/gov/investment/${investmentId}/enable`,
      null
    ).pipe(
      catchError((err: HttpErrorResponse) => {
        console.log('API enableGovIntegration catchError triggered');
        console.log('  - Error status:', err.status);
        console.log('  - Error error:', err.error);
        console.log('  - Error error type:', typeof err.error);
        console.log('  - Has runId:', err.error?.runId);

        // If it's a 400 with validation data, return it as success
        if (err.status === 400 && err.error?.runId) {
          console.log('API: Caught 400 with validation data, treating as success');
          return of(err.error as M.GovEnableResponse);
        }

        // For other errors, re-throw
        console.log('API: Re-throwing error');
        throw err;
      })
    );
  }

  // ==========================================
  // PUBLIC API
  // ==========================================

  /**
   * Warmup endpoint
   * GET /api/public/warmup
   */
  warmup(): Observable<string> {
    return this.http.get<string>(`${this.apiUrl}/api/public/warmup`);
  }

  /**
   * Warmup 2 endpoint
   * GET /api/public/warmup-2
   */
  warmup2(): Observable<string> {
    return this.http.get<string>(`${this.apiUrl}/api/public/warmup-2`);
  }


  /**
   * Get API version
   * GET /api/public/version
   */
  getVersion(): Observable<string> {
    return this.http.get(`${this.apiUrl}/api/public/version`, { responseType: 'text' });
  }

  /**
   * Get public apartments list for investment
   * GET /api/public/apartments/list/{investmentId}
   */
  getPublicApartments(investmentId: number): Observable<M.PageApartmentListItemDto> {
    return this.http.get<M.PageApartmentListItemDto>(
      `${this.apiUrl}/api/public/apartments/list/${investmentId}`
    );
  }

  /**
   * Get public apartment full view
   * GET /api/public/apartments/full/{apartmentId}
   */
  getPublicApartmentFullView(apartmentId: number): Observable<M.ApartmentDetailsDto> {
    return this.http.get<M.ApartmentDetailsDto>(
      `${this.apiUrl}/api/public/apartments/full/${apartmentId}`
    );
  }

  // ==========================================
  // CACHE MANAGEMENT
  // ==========================================

  /**
   * Clear specific cache
   * DELETE /api/v1/cache/{name}
   */
  clearCache(name: string): Observable<Record<string, any>> {
    return this.http.delete<Record<string, any>>(
      `${this.apiUrl}/api/v1/cache/${name}`
    );
  }

  /**
   * Clear all caches
   * DELETE /api/v1/cache/all
   */
  clearAllCaches(): Observable<Record<string, any>> {
    return this.http.delete<Record<string, any>>(
      `${this.apiUrl}/api/v1/cache/all`
    );
  }

  // ==========================================
  // WP SITES ADMIN
  // ==========================================

  /**
   * List all WP Sites for current developer
   * GET /api/v1/wp/sites
   */
  getWpSites(): Observable<WpModels.WpSiteListItem[]> {
    return this.http.get<WpModels.WpSiteListItem[]>(
      `${this.apiUrl}/api/v1/wp/sites`
    );
  }

  /**
   * Get WP Site details
   * GET /api/v1/wp/sites/{id}
   */
  getWpSite(id: number): Observable<WpModels.WpSiteDetail> {
    return this.http.get<WpModels.WpSiteDetail>(
      `${this.apiUrl}/api/v1/wp/sites/${id}`
    );
  }

  /**
   * Create a new WP Site
   * POST /api/v1/wp/sites
   */
  createWpSite(data: WpModels.CreateWpSiteCommand): Observable<WpModels.WpSiteDetail> {
    return this.http.post<WpModels.WpSiteDetail>(
      `${this.apiUrl}/api/v1/wp/sites`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Update WP Site
   * PUT /api/v1/wp/sites/{id}
   */
  updateWpSite(id: number, data: WpModels.UpdateWpSiteCommand): Observable<void> {
    return this.http.put<void>(
      `${this.apiUrl}/api/v1/wp/sites/${id}`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Delete WP Site
   * DELETE /api/v1/wp/sites/{id}
   */
  deleteWpSite(id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/api/v1/wp/sites/${id}`
    );
  }

  /**
   * List auth keys for WP Site
   * GET /api/v1/wp/sites/{id}/keys
   */
  getWpSiteAuthKeys(siteId: number): Observable<WpModels.WpAuthKeyListItem[]> {
    return this.http.get<WpModels.WpAuthKeyListItem[]>(
      `${this.apiUrl}/api/v1/wp/sites/${siteId}/keys`
    );
  }

  /**
   * Generate a new auth key (secret shown only once)
   * POST /api/v1/wp/sites/{id}/keys
   */
  generateWpAuthKey(siteId: number, data: WpModels.GenerateAuthKeyCommand): Observable<WpModels.WpAuthKeyWithSecret> {
    return this.http.post<WpModels.WpAuthKeyWithSecret>(
      `${this.apiUrl}/api/v1/wp/sites/${siteId}/keys`,
      data,
      { headers: this.getHeaders() }
    );
  }

  /**
   * Revoke (disable) an auth key
   * DELETE /api/v1/wp/sites/{siteId}/keys/{keyId}
   */
  revokeWpAuthKey(siteId: number, keyId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/api/v1/wp/sites/${siteId}/keys/${keyId}`
    );
  }
}
